/**
 * assets/loft-configurador-accesorio.js
 *
 * Comportamiento del configurador visual reutilizable (snippets/loft-configurador-accesorio.liquid).
 * No depende del tipo de accesorio (nochero, espejo, cojín, etc.) — todo lo específico del
 * producto ya viene resuelto en el bloque JSON que genera el snippet.
 *
 * Decisiones de diseño importantes (para quien edite esto después):
 *
 * 1) El cambio de imagen principal NO se hace manipulando el <img> del carrusel a mano.
 *    Se usa el método público que el propio Dawn ya expone en su componente <media-gallery>
 *    (`setActiveMedia`), el mismo que el tema usa internamente al cambiar de variante con
 *    imagen asociada. Así el zoom, el modal y el slider de miniaturas siguen funcionando
 *    exactamente igual — no se reemplaza nada del carrusel, solo se le pide que muestre otra
 *    foto que ya está entre sus medios.
 *
 * 2) La sección permanece oculta (`hidden`) hasta el primer cambio real de variante disparado
 *    por el cliente. Shopify siempre deja una variante preseleccionada por defecto, así que
 *    "después de elegir la medida" se interpreta como una interacción real, no como el estado
 *    inicial de la página.
 *
 * 3) Dawn publica PUB_SUB_EVENTS.variantChange DESPUÉS de que su propio código ya intentó poner
 *    la imagen destacada de la nueva variante (ver product-info.js, updateMedia()). Por eso este
 *    listener, que corre después, siempre puede "reafirmar" la imagen del accesorio seleccionado
 *    sin pelear una condición de carrera.
 *
 * 4) El accesorio NUNCA se agrega tocando el <form> nativo del producto. Se deja que Dawn agregue
 *    la cama exactamente como siempre. Solo después de que Dawn confirma ese agregado
 *    (PUB_SUB_EVENTS.cartUpdate), este script hace una segunda llamada propia a /cart/add.js para
 *    el accesorio —con la cantidad elegida (1 o 2)— y vuelve a pedirle al cart drawer que se
 *    repinte con la respuesta de esa segunda llamada.
 *
 *    Importante: Dawn publica cartUpdate SIN envolver el payload en una clave `data` (a
 *    diferencia de variantChange, que sí lo hace). Ver product-form.js:
 *    publish(PUB_SUB_EVENTS.cartUpdate, { source, productVariantId, cartData }). Verificado en
 *    vivo: el payload real está en el propio `evt`, no en `evt.data`.
 *
 * 5) Cuando el configurador está presente, se oculta el botón de pago dinámico (Shop Pay /
 *    "Comprar ahora") del formulario del producto, porque ese botón salta directo al checkout
 *    de Shopify sin pasar por este script (limitación de Shopify, no de este código). Esta
 *    ocultación es local a esta página (solo corre si existe `.loft-config` en el DOM).
 *
 * 6) Cantidad (1 o 2): cada tarjeta trae su propio `data-max-qty` (calculado en Liquid a partir
 *    del inventario real del accesorio), así que el botón "2" se deshabilita solo cuando el
 *    inventario controlado no alcanza — sin inventar disponibilidad que el carrito no pueda
 *    cumplir. Si aun así el carrito rechaza la cantidad (p. ej. alguien más compró la última
 *    unidad segundos antes), se muestra un mensaje de error visible en vez de fallar en silencio.
 */
(function () {
  function initConfigurator(root) {
    var metaEl = root.querySelector('[data-loft-config-meta]');
    var meta;
    try {
      meta = JSON.parse(metaEl.textContent);
    } catch (e) {
      return;
    }

    var sectionId = meta.sectionId;
    var totalEl = root.querySelector('[data-loft-config-total]');
    var errorEl = root.querySelector('[data-loft-config-error]');
    var propertyInput = root.querySelector('[data-loft-config-property]');
    var propertyName = root.dataset.propertyName || 'Accesorio';
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-loft-card]'));
    var qtyWrap = root.querySelector('[data-loft-config-qty]');
    var qtyButtons = Array.prototype.slice.call(root.querySelectorAll('[data-loft-qty-btn]'));
    var availabilityWrap = root.querySelector('[data-loft-config-availability]');
    var availabilityBadge = root.querySelector('[data-loft-config-availability-badge]');
    var optionsByVariantId = {};
    (meta.options || []).forEach(function (opt) {
      optionsByVariantId[opt.variantId] = opt;
    });

    var state = {
      revealed: false,
      currentBedVariantId: null,
      selectedAccessoryVariantId: '', // '' = "Sin accesorio"
      selectedQuantity: 1,
    };

    function getGallery() {
      return document.querySelector('#MediaGallery-' + sectionId);
    }

    function applyMediaFor(accessoryVariantId, bedVariantId) {
      var gallery = getGallery();
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

      var mediaId;
      if (accessoryVariantId && optionsByVariantId[accessoryVariantId]) {
        mediaId = optionsByVariantId[accessoryVariantId].mediaId;
      } else {
        mediaId = meta.baseMediaIdByVariant[bedVariantId];
      }
      if (!mediaId) return;
      gallery.setActiveMedia(mediaId, false);
    }

    function applyTotalFor(accessoryVariantId, bedVariantId, quantity) {
      if (!totalEl) return;
      var priceText;
      if (accessoryVariantId && optionsByVariantId[accessoryVariantId]) {
        var byVariant = optionsByVariantId[accessoryVariantId].priceByVariant[bedVariantId];
        priceText = byVariant && byVariant[String(quantity)];
      } else {
        priceText = meta.basePriceByVariant[bedVariantId];
      }
      if (priceText) totalEl.textContent = priceText;
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function setQuantityUI(card) {
      if (!qtyWrap) return;
      var hasAccessory = !!card.dataset.variantId;
      qtyWrap.hidden = !hasAccessory;
      if (!hasAccessory) return;

      var maxQty = parseInt(card.dataset.maxQty || '2', 10);
      qtyButtons.forEach(function (btn) {
        var qty = parseInt(btn.dataset.qty, 10);
        var disabled = qty > maxQty;
        btn.disabled = disabled;
        btn.classList.toggle('loft-config__qty-btn--disabled', disabled);
      });

      // Si la cantidad elegida antes ya no cabe en el máximo de esta tarjeta, vuelve a 1.
      if (state.selectedQuantity > maxQty) {
        state.selectedQuantity = 1;
      }
      qtyButtons.forEach(function (btn) {
        var qty = parseInt(btn.dataset.qty, 10);
        btn.classList.toggle('is-selected', qty === state.selectedQuantity);
      });
    }

    function setAvailabilityUI(card) {
      if (!availabilityWrap || !availabilityBadge) return;
      var hasAccessory = !!card.dataset.variantId;
      availabilityWrap.hidden = !hasAccessory;
      if (!hasAccessory) return;

      availabilityBadge.textContent = card.dataset.availabilityLabel || '';
      availabilityBadge.className = 'loft-config__badge ' + (card.dataset.availabilityClass || '');
    }

    function updatePropertyInput(card) {
      if (!propertyInput) return;
      var variantId = card.dataset.variantId || '';
      if (!variantId) {
        propertyInput.value = '';
        propertyInput.disabled = true;
        return;
      }
      var label = card.dataset.label || '';
      if (state.selectedQuantity > 1) label += ' x' + state.selectedQuantity;
      propertyInput.value = label;
      propertyInput.disabled = false;
    }

    function refreshForCurrentSelection() {
      applyMediaFor(state.selectedAccessoryVariantId, state.currentBedVariantId);
      applyTotalFor(state.selectedAccessoryVariantId, state.currentBedVariantId, state.selectedQuantity);
    }

    function selectCard(card) {
      cards.forEach(function (c) {
        c.classList.remove('is-selected');
        c.setAttribute('aria-selected', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-selected', 'true');
      clearError();

      var variantId = card.dataset.variantId || '';
      state.selectedAccessoryVariantId = variantId;
      state.selectedQuantity = variantId ? 1 : 0;

      setQuantityUI(card);
      setAvailabilityUI(card);
      updatePropertyInput(card);
      refreshForCurrentSelection();
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        if (card.hasAttribute('disabled')) return;
        selectCard(card);
      });
    });

    qtyButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        state.selectedQuantity = parseInt(btn.dataset.qty, 10);
        qtyButtons.forEach(function (b) {
          b.classList.toggle('is-selected', b === btn);
        });
        var selectedCard = cards.filter(function (c) {
          return c.classList.contains('is-selected');
        })[0];
        if (selectedCard) updatePropertyInput(selectedCard);
        refreshForCurrentSelection();
      });
    });

    function reveal() {
      if (state.revealed) return;
      state.revealed = true;
      root.hidden = false;
      window.requestAnimationFrame(function () {
        root.classList.add('is-visible');
      });
    }

    function onVariantChange(variant) {
      if (!variant) return;
      state.currentBedVariantId = String(variant.id);
      reveal();
      refreshForCurrentSelection();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
        onVariantChange(evt && evt.data && evt.data.variant);
      });
    }

    // --- Botón de pago dinámico: oculto mientras este configurador exista en la página. ---
    var productForm = root.closest('product-info') || document;
    productForm.querySelectorAll('.shopify-payment-button').forEach(function (el) {
      el.style.display = 'none';
    });

    // --- Agregar el accesorio (con su cantidad) justo después de que Dawn agregó la cama. ---
    var bedVariantIds = Object.keys(meta.basePriceByVariant || {});
    var pendingAdd = false;

    function addAccessoryToCart(accessoryVariantId, quantity) {
      if (pendingAdd) return;
      pendingAdd = true;

      var cartComponent = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var body = {
        items: [
          {
            id: accessoryVariantId,
            quantity: quantity,
            properties: { _loft_config_de: propertyName },
          },
        ],
      };
      if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
        body.sections = cartComponent.getSectionsToRender().map(function (s) {
          return s.id;
        });
        body.sections_url = window.location.pathname;
      }

      fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (response) {
          if (response && response.status) {
            showError(
              'Agregamos la cama, pero no pudimos agregar el accesorio elegido: ' +
                (response.description || response.message || 'sin stock suficiente') +
                '. Revisa el carrito.'
            );
            return;
          }
          if (cartComponent && typeof cartComponent.renderContents === 'function') {
            cartComponent.renderContents(response);
          }
        })
        .catch(function (err) {
          showError('Agregamos la cama, pero hubo un problema de conexión al agregar el accesorio. Revisa el carrito.');
          console.error('loft-configurador-accesorio: no se pudo agregar el accesorio al carrito', err);
        })
        .finally(function () {
          pendingAdd = false;
        });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
        var data = evt;
        if (!data || data.source !== 'product-form') return;
        if (bedVariantIds.indexOf(String(data.productVariantId)) === -1) return;
        if (!state.selectedAccessoryVariantId || state.selectedQuantity < 1) return;
        addAccessoryToCart(state.selectedAccessoryVariantId, state.selectedQuantity);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-loft-config]').forEach(initConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
